import React, { useState } from 'react';
import { Button, Input, Tooltip } from 'antd';
import { LikeOutlined, DislikeOutlined, ReadOutlined } from '@ant-design/icons';
import { parseAnswer } from './answerMarkup';
import { rateAnswer, forwardQuestion } from './assistantApi';

/**
 * One answer of the assistant, with its references made clickable and the feedback buttons.
 * onRef(helpId): highlight an element in the app. onStep(tourId, stepNumber): open that help step.
 */
const AssistantAnswer = ({ id, text, covered, fetchFn, onRef, onStep }) => {
  const [rating, setRating] = useState(null);
  const [sent, setSent] = useState('');
  const [sending, setSending] = useState(false);
  const [note, setNote] = useState(null); // with a 👎: the comment being written (null: no box)
  const [noted, setNoted] = useState('');
  const [sendingNote, setSendingNote] = useState(false);

  const rate = (value) => {
    setRating(value);
    rateAnswer(fetchFn, id, value); // the rating counts right away, with or without a comment
    setNote(value === -1 ? '' : null);
    setNoted('');
  };

  const sendNote = async () => {
    setSendingNote(true);
    const result = await rateAnswer(fetchFn, id, -1, note.trim());
    setSendingNote(false);
    if (result.ok) setNote(null);
    setNoted(result.ok ? 'Thanks, noted.' : result.error);
  };

  const askAdmin = async () => {
    setSending(true);
    const result = await forwardQuestion(fetchFn, id);
    setSending(false);
    setSent(result.ok ? result.data.detail : result.error);
  };

  const needsAdmin = !covered || rating === -1;

  return (
    <div className={`as-a${covered ? '' : ' as-uncovered'}`}>
      <div className="as-text">
        {parseAnswer(text).map((part, i) => {
          if (part.type === 'bold') return <b key={i}>{part.text}</b>;
          if (part.type === 'code') return <code key={i}>{part.text}</code>;
          if (part.type === 'ref') {
            return (
              <span key={i} className="as-ref" onClick={() => onRef(part.helpId)} title="Show me where">
                {part.text}
              </span>
            );
          }
          if (part.type === 'step') {
            return (
              <span key={i} className="as-step" onClick={() => onStep(part.tourId, part.step)} title="Open in the help">
                <ReadOutlined /> {part.text}
              </span>
            );
          }
          return <span key={i}>{part.text}</span>;
        })}
      </div>
      <div className="as-actions">
        <Tooltip title="Helpful">
          <Button size="small" type="text" className={rating === 1 ? 'as-on' : ''} icon={<LikeOutlined />} onClick={() => rate(1)} />
        </Tooltip>
        <Tooltip title="Not helpful">
          <Button size="small" type="text" className={rating === -1 ? 'as-on' : ''} icon={<DislikeOutlined />} onClick={() => rate(-1)} />
        </Tooltip>
        {sent ? (
          <span className="as-sent">{sent}</span>
        ) : (
          <Button size="small" type={needsAdmin ? 'primary' : 'link'} loading={sending} onClick={askAdmin}>
            Ask an admin
          </Button>
        )}
      </div>
      {note !== null && (
        <div className="as-note">
          <Input.TextArea autoSize={{ minRows: 2, maxRows: 5 }} maxLength={2000} autoFocus value={note}
            placeholder="What was wrong or missing? (optional)" onChange={(e) => setNote(e.target.value)} />
          <div className="as-note-actions">
            <Button size="small" type="primary" disabled={!note.trim()} loading={sendingNote} onClick={sendNote}>Send</Button>
            <Button size="small" type="link" onClick={() => setNote(null)}>No comment</Button>
          </div>
        </div>
      )}
      {noted && <span className="as-sent">{noted}</span>}
    </div>
  );
};

export default AssistantAnswer;
